import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { getMethod, setAuthorizationLang } from "../../../utils/services";
import JobHeader from "../../../Components/Header/JobHeader";
import Image from "next/image";
import Link from "next/link";
import AppRatingSection from "../../../Components/AppRating/AppRating";
import Head from "next/head";
import sanitize from "sanitize-html";
import styles from "../../job/jobDetails.module.scss";

const toProperNoun = (string) => {
  //This function capitalises first letter of each word
  return string
    .trim()
    .split(' ')
    .map(w => w?.[0]?.toUpperCase() + w?.slice(1))
    .join(' ');
}

const JobCard = ({ id, logo, companyName, location, title, isRemote, isInternship, isPartTime, expReq, exp }) => {
  return (
    <div style={{
      border: '0.8px solid #BDBDBD',
      borderRadius: '8px',
      backgroundColor: 'white',
      padding: '24px 24px 16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flexShrink: 0, width: '46.5px', height: '46.5px', borderRadius: '4px', overflow: 'hidden', backgroundColor: logo ? 'transparent' : '#FF7D3D' }}>
          {logo ?
            <Image src={`${process.env.IMG}${logo}`} alt={`${companyName}'s logo`} width={46.5} height={46.5} />
            : null
          }
        </div>

        <div>
          <div style={{ display: 'flex', gap: '7.24px' }}>
            <img src='/images/icon/briefcase.svg' alt='briefcase icon' width={11.26} height={11.26} style={{ flexShrink: 0, marginTop: '2px' }} />
            <h6 style={{ fontWeight: 700, fontSize: '14.5px', lineHeight: '18px', color: 'black' }}>
              {companyName}
            </h6>
          </div>

          <div style={{ marginTop: '3px', marginInlineStart: '0.9px', display: 'flex', gap: '5.63px' }}>
            <img src='/images/icon/location.svg' alt='location icon' width={9.05} height={10.86} style={{ flexShrink: 0 }} />
            <p style={{ fontSize: '11.25px', lineHeight: '14px', color: 'black' }}>
              {location?.city ?? ''}
              {(location?.city && location?.state) ? `, ${location?.state}` : (location?.state ?? '')}
              {(location?.city && location?.country) ? `, ${location?.country}` : (location?.country ?? '')}
            </p>
          </div>
        </div>
      </div>

      <h6 style={{ marginTop: '11px', fontWeight: 700, fontSize: '14.5px', lineHeight: '18px', color: 'black' }}>
        {toProperNoun(title)}
      </h6>

      <div style={{ marginTop: '13.5px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ borderRadius: '1.5px', backgroundColor: '#EBF0F3', padding: '3.22px 6.44px', fontSize: '11.25px', lineHeight: '14px', color: 'black' }}>
          {isInternship ? 'Internship' : isPartTime ? 'Part Time' : 'Full Time'}
        </div>

        <div style={{ borderRadius: '1.5px', backgroundColor: '#EBF0F3', padding: '3.22px 6.44px', fontSize: '11.25px', lineHeight: '14px', color: 'black' }}>
          {isRemote ? 'Remote' : 'On Site'}
        </div>

        <div style={{ borderRadius: '1.5px', backgroundColor: '#EBF0F3', padding: '3.22px 6.44px', fontSize: '11.25px', lineHeight: '14px', color: 'black' }}>
          {expReq == "Experienced" ? `${exp.min} - ${exp.max} years` : 'Fresher'}
        </div>
      </div>

      <Link href={`/en/job/${id}`}>
        <button style={{ marginTop: '13.5px', width: '100%', height: '32px', borderRadius: '6px', backgroundColor: '#0080FF', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 700, fontSize: '11.25px', lineHeight: '14px', color: 'white' }}>
          View Job
        </button>
      </Link>
    </div>
  );
}

export default function JobPage({ jobDetails }) {
  const router = useRouter();
  const { id } = router.query;

  const socialMediaLinks = [
    {
      name: 'facebook',
      link: 'https://www.facebook.com/skatch.com.official'
    },
    {
      name: 'linkedin',
      link: 'https://www.linkedin.com/company/skatch'
    },
    {
      name: 'tiktok',
      link: 'https://www.tiktok.com/@skatch.com'
    },
    {
      name: 'instagram',
      link: 'https://www.instagram.com/skatch.com.official/'
    },
    // {
    //   name: 'whatsapp',
    //   link: ''
    // }
  ];

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: 'Check out this job on skatch.com',
          url: window.location.href
        });
      } catch (e) {
        console.error("Error sharing:", e);
      }
    } else {
      // fallback for unsupported browsers
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('URL copied to clipboard!');
      } catch {
        alert('Share not supported. Please copy the URL manually.');
      }
    }
  }

  const [highlightTab, setHighlightTab] = useState('description');
  const descRef = useRef(null);
  const incenRef = useRef(null);
  const postedRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref.current) {
      const offsetTop = ref.current.getBoundingClientRect().top + window.pageYOffset;
      const offset = 110; // scroll 110px below top
      window.scrollTo({
        top: offsetTop - offset,
        behavior: 'smooth',
      });
    }
  };

  const [upArrowVisible, setUpArrowVisible] = useState(false);

  const formatDate = (date) => {
    const parts = new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    }).split(' ');

    return `${parts[0]} ${parts[1]}, ${parts[2]}`
  }

  const [latestJobs, setLatestJobs] = useState([]);
  const fetchLatestJobs = async () => {
    setAuthorizationLang('en');
    let res = await getMethod('/job/getLatestJobs?limit=6');

    if (res?.success) {
      setLatestJobs(res?.data);
    }
  }

  const [googlePlayData, setGooglePlayData] = useState(null);
  const fetchGooglePlayData = async () => {
    try {
      const response = (await fetch('https://play.rajkumaar.co.in/json?id=com.skatch.app'));
      const res = await response.json();

      const formattedDownloads = () => {
        let parts = res?.downloads?.split(',');
        return `${parts[0]}${parts.length == 2 ? 'K' : parts.length == 3 ? 'M' : parts.length == 4 ? 'B' : ''}${parts.length == 1 ? '' : '+'}`;
      }

      setGooglePlayData({
        downloads: formattedDownloads(),
        rating: parseFloat(res?.rating || '4.3'),
        noOfRatings: parseInt(res?.noOfUsersRated.split(',').join('')) > 2780 ? res?.noOfUsersRated : '2.77K'
      });
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchLatestJobs();
    fetchGooglePlayData();

    const handleScroll = () => {
      const sections = [
        { id: 'description', ref: descRef },
        { id: 'incentives', ref: incenRef },
        { id: 'posted', ref: postedRef }
      ];

      let closestSection = 'description';
      let minDistance = Infinity;

      sections.forEach(({ id, ref }) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const distance = rect.top;

          if (rect.bottom > 80 && rect.top <= window.innerHeight && distance < minDistance) {
            minDistance = distance;
            closestSection = id;
          }
        }
      });

      setHighlightTab(closestSection);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    const toggleVisibility = () => {
      setUpArrowVisible(window.scrollY > 100); // Show if scrolled down > 100px
    };
    window.addEventListener('scroll', toggleVisibility);

    return () => {
      window.removeEventListener('scroll', handleScroll);

      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  const jobPostingJsonLd = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": toProperNoun(jobDetails?.title),
    "description": sanitize(jobDetails?.jobDescription, {
      allowedTags: [],
      allowedAttributes: []
    }),
    "identifier": {
      "@type": "PropertyValue",
      "name": "Skatch",
      "value": jobDetails?.jobId
    },
    "datePosted": jobDetails?.jobLiveDateAt.split('T')[0],
    "validThrough": jobDetails?.jobLiveDateAt ? new Date(new Date(jobDetails?.jobLiveDateAt).setDate(new Date(jobDetails?.jobLiveDateAt).getDate() + 30)).toISOString().split(":").slice(0, 2).join(":") : "",
    "applicantLocationRequirements": jobDetails?.remoteJob ? {
      "@type": "Country",
      "name": "Anywhere"
    } : null,
    "jobLocationType": jobDetails?.remoteJob ? "TELECOMMUTE" : null,
    "employmentType": jobDetails?.isInternship ? "INTERN" : jobDetails?.partTime ? "PART_TIME" : "FULL_TIME",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Skatch",
      "sameAs": "https://www.skatch.com",
      "logo": "https://www.skatch.com/images/job/ab-logo.svg"
    },
    "jobLocation": jobDetails?.remoteJob ? null : {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": jobDetails?.city?.translations?.[0]?.cityTranslation?.name,
        "addressRegion": jobDetails?.jobState?.translations?.[0]?.stateTranslation?.name,
        "addressCountry": jobDetails?.jobCountry?.translations?.[0]?.countryTranslation?.name
      }
    },
    "baseSalary": {
      "@type": "MonetaryAmount",
      "currency": jobDetails?.salary?.currency?.currencyCode,
      "value": {
        "@type": "QuantitativeValue",
        "minValue": jobDetails?.salary?.lowValue,
        "maxValue": jobDetails?.salary?.upperValue,
        "unitText": "MONTH"
      }
    }
  };

  return (
    <>
      <Head>
        <title>
          {toProperNoun(jobDetails?.title)} in {jobDetails?.company?.companyName}{(jobDetails?.city || jobDetails?.jobState || jobDetails?.jobCountry) ? ', ' : ''}{jobDetails?.city?.translations?.[0]?.cityTranslation?.name ?? ''}{(jobDetails?.city && jobDetails?.jobState) ? `, ${jobDetails?.jobState?.translations?.[0]?.stateTranslation?.name}` : (jobDetails?.jobState?.translations?.[0]?.stateTranslation?.name ?? '')}{(jobDetails?.city && jobDetails?.jobCountry) ? `, ${jobDetails?.jobCountry?.translations?.[0]?.countryTranslation?.name}` : (jobDetails?.jobCountry?.translations?.[0]?.countryTranslation?.name ?? '')}
        </title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />
        <meta
          name="description"
          content={sanitize(jobDetails?.jobDescription, {
            allowedTags: [],
            allowedAttributes: []
          })}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingJsonLd) }}
        />
      </Head>
      <main id={styles.main}>
        <JobHeader dynamicSlug={`/job/${id}`} />

        <section
          id={styles.wrapperSection}
        >
          <div>
            {/* <button style={{ marginInlineStart: '160px', fontWeight: 600, fontSize: '14px', lineHeight: '18.5px', color: '#0080FF' }}>
              Show All Jobs
            </button> */}

            {/* Jobs list section on desktop */}
            <div id={styles.jobListSectionDesktop} style={{ marginTop: 'calc(16px + 19px)' }}>
              {latestJobs?.length ?
                latestJobs
                  .filter(item => item.id != id)
                  .slice(0, 5)
                  .map((item, index) => (
                    <JobCard
                      key={index}
                      id={item?.id}
                      companyName={item?.company}
                      isRemote={item?.isRemote}
                      location={item?.location}
                      logo={item?.logo}
                      title={item?.title}
                      isInternship={item?.isInternship}
                      isPartTime={item?.isPartTime}
                      expReq={item?.experienceRequired}
                      exp={item?.experience}
                    />
                  ))
                : null
              }
            </div>
          </div>

          {/* Job details section */}
          <div id={styles.jobDetailsSection}>
            <div id={styles.companyName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '50%', overflow: 'hidden', backgroundColor: jobDetails?.company?.logo ? 'transparent' : '#FF7D3D' }}>
                {jobDetails?.company?.logo ?
                  <Image src={`${process.env.IMG}${jobDetails?.company?.logo}`} alt={`${jobDetails?.company?.companyName}'s logo`} width={30} height={30} />
                  :
                  null
                }
              </div>

              {jobDetails?.company?.companyName}
            </div>

            <div id={styles.heading} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
              <div>
                <h1 id={styles.title} style={{ color: '#141514' }}>
                  {toProperNoun(jobDetails?.title)}
                </h1>

                <p id={styles.location} style={{ fontWeight: 500, color: '#1D1E1D' }}>
                  {jobDetails?.city?.translations?.[0]?.cityTranslation?.name ?? ''}
                  {(jobDetails?.city && jobDetails?.jobState) ? `, ${jobDetails?.jobState?.translations?.[0]?.stateTranslation?.name}` : (jobDetails?.jobState?.translations?.[0]?.stateTranslation?.name ?? '')}
                  {(jobDetails?.city && jobDetails?.jobCountry) ? `, ${jobDetails?.jobCountry?.translations?.[0]?.countryTranslation?.name}` : (jobDetails?.jobCountry?.translations?.[0]?.countryTranslation?.name ?? '')}
                  {` (${jobDetails?.remoteJob ? 'Remote' : 'On Site'})`}
                </p>
              </div>

              <a className="d-none d-md-block" href={process.env.APP_LINK} target="_blank" rel="noopener noreferrer">
                <button style={{ marginInlineEnd: '-20px', width: '135px', height: '40px', borderRadius: '56px', backgroundColor: '#FF7D3D', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 600, fontSize: '14.75px', lineHeight: '18px', color: 'white' }}>
                  Apply on the App
                </button>
              </a>
            </div>

            <div id={styles.box} style={{ border: '1.85px solid #E7F0FA', borderRadius: '8px' }}>
              <div>
                <Image src='/images/icon/briefcase2.svg' alt='briefcase icon' width={29.66} height={29.66} />

                <div className={styles.text} style={{ marginTop: '4px', color: '#4E4E4E' }}>
                  <span style={{ fontWeight: 700 }}>Job Type:</span>
                  <br />
                  <span style={{ fontWeight: 500 }}>
                    {jobDetails?.isInternship ? 'Internship' : jobDetails?.partTime ? 'Part Time' : 'Full Time'}
                  </span>
                </div>
              </div>

              <div>
                <Image src='/images/icon/timer.svg' alt='timer icon' width={29.66} height={29.66} />

                <div className={styles.text} style={{ marginTop: '4px', color: '#4E4E4E' }}>
                  <span style={{ fontWeight: 700 }}>Experience:</span>
                  <br />
                  <span style={{ fontWeight: 500 }}>
                    {jobDetails?.experienceRequired == 'Experienced' ?
                      jobDetails?.experience?.max ? `${jobDetails?.experience?.min} - ${jobDetails?.experience?.max} Years` : `${jobDetails?.experience.min}+ Years`
                      : 'Fresher'
                    }
                  </span>
                </div>
              </div>

              <div>
                <Image src='/images/icon/graduation-cap.svg' alt='graduation cap icon' width={24} height={24} />

                <div className={styles.text} style={{ marginTop: '10px', color: '#4E4E4E' }}>
                  <span style={{ fontWeight: 700 }}>Education:</span>
                  <br />
                  <span style={{ fontWeight: 500 }}>{jobDetails?.education?.translations?.[0]?.educationTranslation?.label}</span>
                </div>
              </div>

              <div>
                <Image src='/images/icon/paycheck.svg' alt='paycheck icon' width={24} height={24} />

                <div className={styles.text} style={{ marginTop: '10px', color: '#4E4E4E' }}>
                  <span style={{ fontWeight: 700 }}>Salary:</span>
                  <br />
                  <span style={{ fontWeight: 500 }}>{jobDetails?.salary?.lowValue} - {jobDetails?.salary?.upperValue} {jobDetails?.salary ? (jobDetails?.salary?.currency?.en?.symbol ?? 'E£') : ''}</span>
                </div>
              </div>
            </div>

            <div id={"scrollContainer"}>
              <div id={styles.tabs}>
                <div
                  className={`${styles.tab} ${highlightTab == 'description' ? styles.highlight : ''}`}
                  onClick={() => scrollToSection(descRef)}
                >
                  Job Description
                </div>
                <div
                  className={`${styles.tab} ${highlightTab == 'incentives' ? styles.highlight : ''}`}
                  onClick={() => scrollToSection(incenRef)}
                >
                  Incentives
                </div>
                <div
                  className={`${styles.tab} ${highlightTab == 'posted' ? styles.highlight : ''}`}
                  onClick={() => scrollToSection(postedRef)}
                >
                  Job Posted
                </div>
              </div>

              <div id="description" className={styles.description} ref={descRef}>
                <h2>
                  Job Description
                </h2>
                <div style={{ marginTop: '22px', fontWeight: 500, color: '#141514' }} dangerouslySetInnerHTML={{ __html: jobDetails?.jobDescription }} />
              </div>

              <hr id={styles.hr1} style={{ borderColor: '#B8BDBB' }} />

              <div id="incentives" ref={incenRef}>
                <h2>
                  Incentives
                </h2>
                <div id={styles.incentivesList}>
                  {jobDetails?.benefits?.length ?
                    jobDetails?.benefits?.map((b, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '11px', fontSize: '14px', lineHeight: '19px', color: '#4E4E4E' }}>
                        <img src='/images/icon/incentives-bullet.svg' alt='incentive icon' width={20} height={20} style={{ flexShrink: 0 }} />
                        {b?.translations?.[0]?.jobBenefitTranslation?.name}
                      </div>
                    ))
                    : null
                  }
                </div>
              </div>

              <hr id={styles.hr2} style={{ borderColor: '#B8BDBB' }} />

              <div id="posted" className={styles.posted} ref={postedRef}>
                <img src='/images/icon/calendar-blank.svg' alt='blank calendar icon' width={29.66} height={29.66} />
                <div style={{ color: '#4E4E4E' }}>
                  <div style={{ fontWeight: 700 }}>Job Posted:</div>
                  <div style={{ fontWeight: 500 }}>{formatDate(jobDetails?.jobLiveDateAt)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Banners section */}
          <div className="d-none d-md-block" style={{ marginTop: '44px' }}>
            <div style={{ boxShadow: '0px 1px 2px 0px #00000008', border: '1px solid #1414140D', borderRadius: '8px', backgroundColor: '#004E52D4', backgroundImage: 'url("/images/logo/logo-transparent1.svg")', padding: '24px 24px 0 24px' }}>
              <h3 style={{ fontWeight: 700, fontSize: '20px', lineHeight: '30px', color: '#FFB93A' }}>
                Easy Apply
              </h3>

              <ul style={{ marginTop: '4px', listStyleType: 'none', fontWeight: 700, fontSize: '14px', lineHeight: '21px', color: 'white' }}>
                <li>Personalised job matches</li>
                <li>Easy CV creation</li>
                <li>Direct connects with HR</li>
              </ul>

              <div style={{ marginTop: '35px', display: 'flex', justifyContent: 'center', position: 'relative' }}>
                <Image src='/images/app-in-phone.svg' alt='app open in phone' width={131} height={225} />

                <a href={process.env.APP_LINK} target="_blank" rel="noopener noreferrer">
                  <button style={{ position: 'absolute', top: '105px', insetInlineStart: 0, width: '202px', height: '40px', borderRadius: '4px', backgroundColor: '#FF7D3D', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 500, fontSize: '16px', lineHeight: '24px', color: 'white' }}>
                    Download the App
                  </button>
                </a>
              </div>
            </div>

            <div style={{ marginTop: '44px', borderRadius: '10px', backgroundColor: 'white', backgroundImage: 'url("/images/logo/logo-transparent.svg")', backgroundRepeat: 'no-repeat', backgroundPosition: 'center right', padding: '20px 17px' }}>
              <h3 style={{ fontWeight: 700, fontSize: '20px', lineHeight: '27.5px', color: '#FF7D3D' }}>
                Why apply on the Skatch App?
              </h3>

              <ul style={{ marginTop: '30px', listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '15px', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#323232' }}>
                <li style={{ display: 'flex', gap: '9px' }}>
                  <img src='/images/icon/phone.svg' alt='phone icon' width={13} height={20.4} style={{ marginTop: '3.5px' }} />
                  Easy Apply with one tap
                </li>

                <li style={{ display: 'flex', gap: '9px' }}>
                  <img src='/images/icon/save.svg' alt='save icon' width={13} height={16} style={{ marginTop: '3.5px' }} />
                  Save jobs and track your applications
                </li>

                <li style={{ display: 'flex', gap: '9px' }}>
                  <img src='/images/icon/location2.svg' alt='location icon' width={13} height={16.3} style={{ marginTop: '3.5px' }} />
                  Get instant job alerts based on your location
                </li>

                <li style={{ display: 'flex', gap: '9px' }}>
                  <img src='/images/icon/cv.svg' alt='cv icon' width={13} height={15.54} style={{ marginTop: '3.5px' }} />
                  Build your CV while chatting for FREE
                </li>
              </ul>

              <a href={process.env.APP_LINK} target="_blank" rel="noopener noreferrer" style={{ width: '100%' }}>
                <button style={{ marginTop: '15px', borderRadius: '4px', backgroundColor: '#0080FF', width: '100%', height: '36px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px', color: 'white' }}>
                  Create profile
                  <Image src='/images/icon/arrow-white.svg' alt='right arrow icon' width={5.96} height={8.41} />
                </button>
              </a>
            </div>
          </div>

          {/* Job list section on mobile */}
          <div
            className="d-block d-md-none"
            style={{ marginTop: '84px' }}
          >
            <h2 style={{ marginInlineStart: '10px', fontWeight: 500, fontSize: '20px', lineHeight: '24px', color: 'black' }}>
              Recent Activities
            </h2>
            <div style={{ marginTop: '16px', width: '275px', marginInline: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {latestJobs?.length ?
                latestJobs
                  .filter(item => item.id != id)
                  .slice(0, 5)
                  .map((item, index) => (
                    <JobCard
                      key={index}
                      id={item?.id}
                      companyName={item?.company}
                      isRemote={item?.isRemote}
                      location={item?.location}
                      logo={item?.logo}
                      title={item?.title}
                      isInternship={item?.isInternship}
                      isPartTime={item?.isPartTime}
                      expReq={item?.experienceRequired}
                      exp={item?.experience}
                    />
                  ))
                : null
              }
            </div>
          </div>
        </section>

        {/* Rating and reviews section */}
        <AppRatingSection marginTop='68px' googlePlayData={googlePlayData} />

        <footer id={styles.footer} style={{ backgroundColor: '#004E52', position: 'relative' }}>
          <div className="d-md-flex" style={{ gap: '35px' }}>
            <div className="d-none d-md-block" style={{ marginTop: '12.5px' }}>
              <Image src='/images/logo/logo-white.svg' alt='skatch logo' width={133} height={40} />
            </div>
            <div className="d-block d-md-none" style={{ width: 'fit-content', marginInline: 'auto' }}>
              <Image src='/images/logo/logo-white.svg' alt='skatch logo' width={133} height={40} />
            </div>

            <hr className="d-block d-md-none" style={{ marginBlock: '18px', border: '1px solid #D8D8D880' }} />

            <div>
              <h3 className="text-center text-md-left" style={{ fontWeight: 600, fontSize: '20px', lineHeight: '24px', color: 'white' }}>
                Follow us on social media
              </h3>

              <div id={styles.socialMediaLinks} style={{ display: 'flex', gap: '15px' }}>
                {socialMediaLinks.map((item, index) => (
                  <a key={index} href={item.link} target="_blank" style={{ width: '41px', height: '41px', border: '1.62px solid #FFB93A50', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img src={`/images/icon/${item.name}-yellow.svg`} alt={`${item.name} icon`} />
                  </a>
                ))}
              </div>

              <hr className="d-block d-md-none" style={{ marginBlock: '18px', border: '1px solid #D8D8D880' }} />

              <p>
                For any inquiries, reach us at: <a href='mailto:hello@skatch.com' style={{ textDecoration: 'underline' }}>hello@skatch.com</a>
              </p>
            </div>
          </div>

          <hr className="d-none d-md-block" style={{ marginInlineStart: '4px', marginBlock: '16px 28px', width: '492px', borderColor: '#D8D8D880' }} />

          <div className="d-none d-md-flex" style={{ marginInlineStart: '4px', width: '492px', paddingInlineEnd: '18px', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', lineHeight: '16px', color: 'white' }}>
            <p>© {new Date().getFullYear()} Skatch | All rights reserved</p>

            <a href='/en/privacy-policy' target="_blank">Privacy Policy</a>

            <a href='/en/terms-and-conditions' target="_blank">Terms & Conditions</a>
          </div>

          <div className="d-none d-md-block" style={{ position: 'absolute', insetInlineEnd: '96px', top: '50%', transform: 'translateY(-50%)', border: '1px solid #0080FF', borderRadius: '18px', backgroundColor: 'white', padding: '28px 138px 28px 28px' }}>
            <h3 style={{ fontWeight: 700, fontSize: '20px', lineHeight: '24px', color: '#190A28' }}>
              Apply on Skatch App
            </h3>

            <p style={{ marginTop: '8px', fontSize: '16px', lineHeight: '20px', color: '#190A28' }}>
              Get real time job updates on our App
            </p>

            <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* <img src='/images/download-from-app-store.svg' alt='download from app store button' /> */}

              <a href={process.env.APP_LINK} target="_blank" rel="noopener noreferrer">
                <img src='/images/download-from-google-play.svg' alt='download from google play button' />
              </a>
            </div>
          </div>
        </footer>

        <div className="d-block d-md-none" style={{ backgroundColor: '#F9F9F9', padding: '18px 18px 25px', position: 'relative' }}>
          <h3 style={{ fontWeight: 700, fontSize: '14px', lineHeight: '24px', color: '#190A28' }}>
            Apply on Skatch App
          </h3>

          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontWeight: 700, fontSize: '14px', lineHeight: '20px', color: '#172B4D' }}>
                {googlePlayData?.rating}
                <Image src='/images/icon/star.svg' alt="star icon" width={12} height={11.5} />
              </div>

              <div style={{ marginTop: '2px', fontSize: '10px', lineHeight: '12px', color: '#42526E' }}>
                {googlePlayData?.noOfRatings} reviews
              </div>
            </div>

            <div style={{ marginInline: '8px', height: '34px', border: '1px solid #E8E7EA' }} />

            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', lineHeight: '20px', color: '#172B4D' }}>
                {googlePlayData?.downloads}
              </div>

              <div style={{ marginTop: '2px', fontSize: '10px', lineHeight: '12px', color: '#42526E' }}>
                Downloads
              </div>
            </div>
          </div>

          <div style={{ position: 'absolute', right: '12px', top: '18px' }}>
            <a href={process.env.APP_LINK} target="_blank" rel="noopener noreferrer">
              <img src='/images/download-from-google-play.svg' alt='download from google play button' width={100} height={30} />
            </a>

            {/* <img src='/images/download-from-app-store.svg' alt='download from app store button' width={100} height={30} style={{ marginTop: '6px' }} /> */}
          </div>
        </div>

        <div className="d-flex d-md-none" style={{ position: 'sticky', bottom: 0, width: '100%', backgroundColor: '#F9F9F9', padding: '10px 20px 10px 52px', justifyContent: 'space-between', alignItems: 'center' }}>
          <img src='/images/icon/share.svg' alt="share icon" width={21} height={20}
            onClick={handleShare}
          />

          <a href={`${process.env.WEB_URL}/en/app-link?pathname=job/${jobDetails?._id}`} target="_blank" rel="noreferrer noopener">
            <button style={{ width: '152px', height: '35px', borderRadius: '5px', backgroundColor: '#0080FF', fontWeight: 600, fontSize: '16px', lineHeight: '19px', color: '#F5F5F5' }}>
              Apply For Job
            </button>
          </a>

          {upArrowVisible ?
            <Image src='/images/icon/arrow-circle-up.svg' alt="share icon" width={33} height={33} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
            :
            <div style={{ width: '33px', height: '33px' }} />
          }
        </div>
      </main>
    </>
  );
}

export async function getServerSideProps(context) {
  const { id } = context.query;

  let res = await getMethod(`/job/details/${encodeURIComponent(id)}?lang=en`);

  if (res?.success) {
    return { props: { jobDetails: res?.data } }
  }

  return {
    redirect: {
      destination: '/en',
      permanent: false,
    },
  };
}